import { inflect } from 'inflection'

import type { BulkUploadMode } from '@/utils/bulkContentUpload'

const files = (n: number) => `${n} ${inflect('file', n)}`

const modules = (n: number) => `${n} ${inflect('module', n)}`
const supplementaryFiles = (n: number) => `${n} supplementary ${inflect('file', n)}`

// Her Upload step and banner. V1 drops Pause (decision 49) and Interrupted's Resume and Discard (50);
// the interrupted copy, the running note and the stop toast are ours.
export const bulkUploadToasts = {
  otherTab: 'An upload is already running in another tab.',
  modules: {
    done: (d: number) => `${modules(d)} created as drafts.`,
    cancelled: 'Upload cancelled. Nothing was created.',
    stopped: (d: number, finishing: number) =>
      `Upload stopped. ${modules(d)} created before it stopped.` +
      (finishing > 0
        ? ` ${finishing} more ${inflect('is', finishing, 'is', 'are')} finishing and will appear in the library.`
        : ''),
  },
  supplementary: {
    moduleLocked: 'This module is currently being edited by someone else. Please try again later.',
    lockFailed: "The upload couldn't start. Check your connection and try again.",
    moduleTaken:
      "Someone else is now editing this module, so the remaining files weren't added. Retry once they're done.",
    done: (d: number) => `${supplementaryFiles(d)} added.`,
    cancelled: 'Upload cancelled. Nothing was added.',
    stopped: (d: number, finishing: number) =>
      `Upload stopped. ${files(d)} added before it stopped.` +
      (finishing > 0 ? ` ${finishing} more ${inflect('is', finishing, 'is', 'are')} finishing and will be added.` : ''),
  },
} satisfies { otherTab: string } & Record<BulkUploadMode, unknown>

export const uploadRunningTooltip = 'An upload is already running. Wait for it to finish, or stop it first.'

type Counts = { total: number; completed: number; failed: number; percent: number; didNotUpload: number }

type JobCopy = {
  banner: {
    running: (c: Counts) => string
    finished: (c: Counts) => string
    finishedWithFailures: (c: Counts) => string
    interrupted: (c: Counts) => string
  }
  panel: {
    running: string
    finished: string
    interrupted: string
    interruptedNote: string
    progress: (c: Counts) => string
    finishedNote: (c: Counts) => string
  }
  stop: { title: string; body: string }
  /** Follows the title, which is set in bold. */
  removeBody: (percent: number) => string
  exitBody: string
  refreshBody: string
  channelChangedBody: string
}

const interruptedHeadline = (done: string, c: Counts) =>
  `Upload interrupted · ${done} · ${files(c.didNotUpload)} didn't upload`

export const bulkJobCopy: Record<BulkUploadMode, JobCopy> = {
  modules: {
    banner: {
      running: c => `Uploading ${modules(c.total)} · ${c.completed} created`,
      finished: c => `${modules(c.completed)} created as drafts`,
      finishedWithFailures: c => `${c.completed} of ${modules(c.total)} created · ${c.failed} failed`,
      interrupted: c => interruptedHeadline(`${c.completed} of ${modules(c.total)} created`, c),
    },
    panel: {
      running: 'Creating your modules',
      finished: 'Upload complete',
      interrupted: 'Upload interrupted',
      interruptedNote:
        "The upload stopped when the page closed. Files that didn't upload can be added again with Upload Multiple.",
      progress: c => `${c.completed} of ${modules(c.total)} created`,
      finishedNote: c =>
        `${modules(c.completed)} created as drafts. Open each one in Edit Module to review the generated metadata and publish.`,
    },
    stop: {
      title: 'Stop uploading modules?',
      body: 'Stopping cancels the remaining files. Modules that finished uploading are kept as drafts.',
    },
    removeBody: percent =>
      ` is ${percent}% uploaded. That progress will be discarded and no module will be created from it.`,
    exitBody:
      "Your upload is still running. Logging out stops it: modules that already finished are kept as drafts, and any it started but hadn't finished are removed.",
    refreshBody:
      'The SME Portal has been updated. An upload is running in this tab, and refreshing now stops it. Modules that already finished are kept as drafts.',
    channelChangedBody:
      'Your upload was stopped because the channel changed. Modules that already finished are kept as drafts in the original channel.',
  },
  supplementary: {
    banner: {
      running: c => `Adding ${supplementaryFiles(c.total)} · ${c.completed} added`,
      finished: c => `${supplementaryFiles(c.completed)} added`,
      finishedWithFailures: c => `${c.completed} of ${supplementaryFiles(c.total)} added · ${c.failed} failed`,
      interrupted: c => interruptedHeadline(`${c.completed} of ${files(c.total)} added`, c),
    },
    panel: {
      running: 'Adding your files',
      finished: 'Files added',
      interrupted: 'Upload interrupted',
      interruptedNote:
        "The upload stopped when the page closed. Files that didn't upload can be added again with Add Multiple Files.",
      progress: c => `${c.completed} of ${files(c.total)} added`,
      finishedNote: c => `${files(c.completed)} attached to this module. Open any file to see or change details.`,
    },
    stop: {
      title: 'Stop adding files?',
      body: 'Stopping cancels the remaining files. Files that finished uploading stay attached to the module.',
    },
    removeBody: percent =>
      ` is ${percent}% uploaded. That progress will be discarded and the file won't be added to this module.`,
    exitBody:
      "Your upload is still running. Logging out stops it: files that already finished stay attached to the module, and any it started but hadn't finished are removed.",
    refreshBody:
      'The SME Portal has been updated. An upload is running in this tab, and refreshing now stops it. Files that already finished stay attached to the module.',
    channelChangedBody:
      'Your upload was stopped because the channel changed. Files that already finished stay attached to the module in the original channel.',
  },
}

export const bannerRunningSubline = (percent: number) => `${percent}% · you can keep working while this finishes.`
export const bannerFinishedSubline = 'Open the panel to review each file.'
export const runningNote =
  'Minimize this window to keep working in the portal while your files upload. Keep this browser tab open until the upload finishes.'
export const stillWaiting = (n: number) => `${files(n)} still waiting.`
export const uploadingStatus = (percent: number) => `Uploading ${percent}%`
export const processingStatus = (percent: number) => `Processing ${percent}%`
export const removeTitle = 'Remove this file?'
export const removeSubline = "This can't be undone — you'd need to select the file again."
export const removeTooltip = 'Remove this file'
export const retryAllLabel = (n: number) => `Retry all ${files(n)} that failed`
export const exitTitle = 'Stop the upload and log out?'
/** Another tab's job whose record doesn't say which kind it is. */
export const exitBodyUnknownMode =
  "Your upload is still running. Logging out stops it: anything that already finished uploading is kept, and anything it started but hadn't finished is removed."
export const channelSwitchBlocked = "You can't switch channels while an upload is running."
